<div class="concierge-widget">
  <div *ngIf="loading" class="widget-loading bg-white rounded-2xl border border-rose-100 p-12 text-center shadow-xs">
    <div class="spinner-border spinner-border-sm text-[#B01E3E] mr-2" role="status"></div>
    <span class="font-bold text-gray-700">Connecting to your Express Executive Concierge...</span>
  </div>

  <!-- State 1: Assigned RM & Active Workspace (Split-Panel Executive Layout) -->
  <div *ngIf="!loading && conciergeData && conciergeData.assigned" class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
    
    <!-- 👑 LEFT COLUMN (4 Cols): RM Dossier, Stage Tracker & Executive Shortcuts -->
    <div class="lg:col-span-4 flex flex-col gap-5">
      
      <!-- 1. Dedicated RM Dossier Card -->
      <div class="bg-white rounded-2xl border border-rose-200/80 p-5 shadow-sm relative overflow-hidden">
        <div class="absolute -right-12 -top-12 w-32 h-32 bg-rose-100/50 rounded-full blur-xl pointer-events-none"></div>
        
        <div class="flex items-center justify-between mb-4">
          <span class="px-3 py-1 rounded-full bg-rose-100 text-[#B01E3E] text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5">
            <i class="bi bi-patch-check-fill text-[#B01E3E]"></i> Assigned Matchmaker
          </span>
          <span class="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-emerald-200">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Active
          </span>
        </div>

        <div class="flex items-center gap-4">
          <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#B01E3E] to-rose-700 text-white font-black text-3xl flex items-center justify-center shadow-md shrink-0 relative">
            <span>{{ conciergeData.rm.name.charAt(0) }}</span>
            <div class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white"></div>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="font-display font-bold text-xl text-gray-900 m-0 truncate">{{ conciergeData.rm.name }}</h3>
            <p class="text-xs text-gray-500 m-0 mt-0.5">Senior Relationship Manager</p>
            <span class="inline-block mt-1 bg-amber-100 text-amber-900 text-[10px] font-extrabold px-2 py-0.5 rounded uppercase tracking-wider">
              <i class="bi bi-stars text-amber-600"></i> Express PRO Concierge
            </span>
          </div>
        </div>

        <!-- Direct Contact Action Buttons -->
        <div class="grid grid-cols-2 gap-2.5 mt-5 pt-4 border-t border-gray-100">
          <a [href]="'tel:' + conciergeData.rm.phone" class="py-2.5 px-3 bg-rose-50 hover:bg-[#B01E3E] hover:text-white text-[#B01E3E] rounded-xl text-xs font-bold text-center flex items-center justify-center gap-2 transition-all no-underline shadow-2xs">
            <i class="bi bi-telephone-fill"></i> Direct Call
          </a>
          <a [href]="'mailto:' + conciergeData.rm.email" class="py-2.5 px-3 bg-gray-50 hover:bg-gray-800 hover:text-white text-gray-700 rounded-xl text-xs font-bold text-center flex items-center justify-center gap-2 transition-all no-underline shadow-2xs">
            <i class="bi bi-envelope-fill"></i> Direct Email
          </a>
        </div>
      </div>

      <!-- 2. Matchmaking Progress & Curated Statistics Card -->
      <div class="bg-gradient-to-br from-pink-50/80 via-white to-rose-50/50 rounded-2xl border border-rose-200/60 p-5 shadow-xs">
        <h4 class="text-sm font-bold text-gray-900 m-0 flex items-center justify-between">
          <span class="flex items-center gap-2"><i class="bi bi-graph-up-arrow text-[#B01E3E]"></i> Matchmaking Stage</span>
          <span class="bg-[#B01E3E] text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">PRO</span>
        </h4>

        <div class="mt-3.5 bg-white p-3.5 rounded-xl border border-rose-100 shadow-2xs">
          <div class="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Current Progress Stage</div>
          <div class="text-sm font-black text-[#B01E3E] flex items-center gap-2">
            <i class="bi bi-check-circle-fill text-emerald-500"></i> {{ conciergeData.rm_stage || 'Profiles Shared' }}
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2.5 mt-3">
          <div class="bg-white p-3.5 rounded-xl border border-rose-100 text-center shadow-2xs">
            <div class="text-xl font-black text-gray-900">{{ getSharedProfileCount() }}</div>
            <div class="text-[11px] font-medium text-gray-500 mt-0.5">Profiles Curated</div>
          </div>
          <div class="bg-white p-3.5 rounded-xl border border-rose-100 text-center shadow-2xs">
            <div class="text-xl font-black text-emerald-600">100%</div>
            <div class="text-[11px] font-medium text-gray-500 mt-0.5">Verified Dossiers</div>
          </div>
        </div>
      </div>

      <!-- 3. Executive Shortcuts Card -->
      <div class="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-xs space-y-2.5">
        <h4 class="text-xs font-bold text-gray-400 uppercase tracking-wider m-0 mb-3 flex items-center gap-1.5">
          <i class="bi bi-lightning-charge-fill text-amber-500"></i> Executive Shortcuts
        </h4>
        
        <a routerLink="/partner-preferences" class="w-full py-2.5 px-3.5 bg-gray-50 hover:bg-rose-50 hover:text-[#B01E3E] text-gray-700 rounded-xl text-xs font-bold flex items-center justify-between transition-all no-underline border border-gray-100 shadow-2xs">
          <span class="flex items-center gap-2.5"><i class="bi bi-sliders text-[#B01E3E] text-sm"></i> Adjust Partner Preferences</span>
          <i class="bi bi-chevron-right text-gray-400"></i>
        </a>
        
        <a routerLink="/plans" class="w-full py-2.5 px-3.5 bg-gray-50 hover:bg-rose-50 hover:text-[#B01E3E] text-gray-700 rounded-xl text-xs font-bold flex items-center justify-between transition-all no-underline border border-gray-100 shadow-2xs">
          <span class="flex items-center gap-2.5"><i class="bi bi-patch-check-fill text-amber-500 text-sm"></i> Membership PRO Benefits</span>
          <i class="bi bi-chevron-right text-gray-400"></i>
        </a>
        
        <a routerLink="/my-tickets" class="w-full py-2.5 px-3.5 bg-gray-50 hover:bg-rose-50 hover:text-[#B01E3E] text-gray-700 rounded-xl text-xs font-bold flex items-center justify-between transition-all no-underline border border-gray-100 shadow-2xs">
          <span class="flex items-center gap-2.5"><i class="bi bi-headset text-indigo-500 text-sm"></i> Priority Concierge Support</span>
          <i class="bi bi-chevron-right text-gray-400"></i>
        </a>
      </div>

    </div>

    <!-- 💬 RIGHT COLUMN (8 Cols): The Live Curated Match Showcase & Interactive Chat Suite -->
    <div class="lg:col-span-8 flex flex-col bg-white rounded-2xl border border-rose-200/80 shadow-sm overflow-hidden h-[calc(100vh-140px)] min-h-[640px] max-h-[840px]">
      
      <!-- Chat Suite Header Bar -->
      <div class="bg-gradient-to-r from-pink-50/70 via-white to-pink-50/70 p-4 px-6 border-b border-rose-100 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-3">
          <div class="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></div>
          <div>
            <h3 class="font-bold text-gray-900 text-base m-0 flex items-center gap-2">
              Executive Communication & Showcase Room
            </h3>
            <span class="text-xs text-gray-500">Live chat & curated recommendations with {{ conciergeData.rm.name }}</span>
          </div>
        </div>
        <span class="bg-rose-100 text-[#B01E3E] text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1.5 shrink-0">
          <i class="bi bi-shield-lock-fill"></i> Encrypted Suite
        </span>
      </div>

      <!-- Chat History / Stream -->
      <div class="chat-history flex-1 overflow-y-auto p-6 space-y-5 bg-[#FCF9F8]/40">
        <div *ngIf="conciergeData.messages.length === 0" class="chat-placeholder text-center my-auto max-w-md mx-auto p-8">
          <div class="w-16 h-16 rounded-full bg-rose-100 text-[#B01E3E] flex items-center justify-center text-3xl mx-auto mb-3 shadow-2xs">
            <i class="bi bi-chat-heart-fill"></i>
          </div>
          <h4 class="font-bold text-gray-900 text-base mb-1">Welcome to your Executive Suite</h4>
          <p class="text-xs sm:text-sm text-gray-600 leading-relaxed m-0">
            Your dedicated Personal Match Manager is currently reviewing verified background dossiers for your preferences. Send a message or ask any questions anytime below!
          </p>
        </div>

        <div *ngFor="let msg of conciergeData.messages" class="flex flex-col w-full" [ngClass]="msg.sender_type === 'CLIENT' ? 'items-end' : 'items-start'">
          
          <!-- Standard Text Bubble or Note Header -->
          <div class="max-w-[85%] sm:max-w-[75%] rounded-2xl p-4 shadow-2xs transition-all"
               [ngClass]="msg.sender_type === 'CLIENT' ? 'bg-[#F8E8EC] border border-[#B01E3E]/20 rounded-br-xs text-gray-900' : 'bg-white border border-gray-200/90 rounded-bl-xs text-gray-900'">
            
            <div class="flex items-center justify-between gap-4 mb-1.5 text-[11px]">
              <span class="font-bold" [ngClass]="msg.sender_type === 'CLIENT' ? 'text-[#B01E3E]' : 'text-gray-900'">
                {{ msg.sender_type === 'CLIENT' ? 'You' : conciergeData.rm.name + ' (Personal Matchmaker)' }}
              </span>
              <span class="text-gray-400">{{ msg.created_at | date:'shortTime' }}</span>
            </div>

            <p *ngIf="msg.content" class="text-sm m-0 leading-relaxed">{{ msg.content }}</p>

            <!-- RM Recommendation Note -->
            <div *ngIf="msg.rm_note" class="mt-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200/80 text-amber-900 text-xs flex items-start gap-2.5">
              <i class="bi bi-chat-quote-fill text-amber-600 text-base shrink-0 mt-0.5"></i>
              <span class="leading-relaxed font-medium">{{ msg.rm_note }}</span>
            </div>
          </div>

          <!-- 🌟 Full-Width Curated Match Showcase Grid inside Chat Stream -->
          <div *ngIf="msg.message_type === 'PROFILE_CARD' && msg.shared_profiles?.length > 0" class="w-full mt-3 mb-2">
            <div class="flex items-center justify-between mb-2.5 px-1">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                <i class="bi bi-stars text-amber-500"></i> Hand-Curated Profiles for Evaluation ({{ msg.shared_profiles.length }})
              </span>
            </div>

            <!-- Responsive 2-Column Grid filling entire 8-Col Panel -->
            <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 w-full">
              <div *ngFor="let card of msg.shared_profiles" 
                   class="bg-white border border-gray-200/90 hover:border-[#B01E3E]/60 rounded-2xl p-4 transition-all shadow-xs hover:shadow-md flex flex-col justify-between cursor-pointer group"
                   (click)="openCandidateModal(card)">
                
                <div class="flex items-start gap-3.5">
                  <!-- Avatar/Photo -->
                  <div class="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl overflow-hidden bg-rose-50 border border-gray-200 shrink-0 relative group-hover:scale-105 transition-all">
                    <img *ngIf="card.photo_url" [src]="card.photo_url" alt="Match" class="w-full h-full object-cover" />
                    <div *ngIf="!card.photo_url" class="w-full h-full flex items-center justify-center font-black text-2xl text-[#B01E3E]">
                      {{ card.name.charAt(0) }}
                    </div>
                    <span class="absolute top-1 right-1 bg-amber-400 text-slate-950 text-[9px] font-black px-1.5 py-0.5 rounded shadow-2xs">
                      {{ card.compatibility_score || 85 }}%
                    </span>
                  </div>

                  <!-- Profile Details -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center justify-between gap-2 mb-1">
                      <h5 class="font-bold text-gray-900 text-base m-0 truncate group-hover:text-[#B01E3E] transition-colors">{{ card.name }}</h5>
                      <span *ngIf="card.interest_sent" class="bg-emerald-100 text-emerald-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase shrink-0">
                        <i class="bi bi-check-circle-fill"></i> Interested
                      </span>
                    </div>
                    <p class="text-xs font-semibold text-gray-700 m-0 mb-1">{{ card.age }} Yrs • {{ card.occupation }}</p>
                    <span class="text-[11px] text-gray-400 block truncate flex items-center gap-1">
                      <i class="bi bi-geo-alt-fill text-[#B01E3E]/70"></i> {{ card.location }}
                    </span>
                  </div>
                </div>

                <!-- Action Button Toolbar -->
                <div class="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-gray-100" (click)="$event.stopPropagation()">
                  <button class="py-2 px-2.5 rounded-xl bg-gray-50 hover:bg-rose-50 text-gray-700 hover:text-[#B01E3E] text-[11px] font-bold transition-all border border-gray-200/80 flex items-center justify-center gap-1.5 shadow-2xs"
                          (click)="openCandidateModal(card)">
                    <i class="bi bi-person-lines-fill"></i> View Profile
                  </button>
                  
                  <button *ngIf="!card.interest_sent" 
                          class="py-2 px-2.5 rounded-xl bg-[#B01E3E] hover:bg-rose-900 text-white text-[11px] font-bold transition-all flex items-center justify-center gap-1.5 shadow-2xs"
                          (click)="performRecommendationAction('INTEREST', card)">
                    <i class="bi bi-heart-fill"></i> I'm Interested
                  </button>
                  <button *ngIf="card.interest_sent" disabled class="py-2 px-2.5 rounded-xl bg-emerald-50 text-emerald-700 text-[11px] font-bold flex items-center justify-center gap-1 opacity-80 cursor-default">
                    <i class="bi bi-check-lg"></i> Sent
                  </button>

                  <button class="py-2 px-2.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 text-[11px] font-bold transition-all border border-amber-200/80 flex items-center justify-center gap-1.5 shadow-2xs"
                          (click)="askRmAboutCard(card, $event)">
                    <i class="bi bi-chat-dots-fill"></i> Ask RM
                  </button>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Chat Input Toolbar -->
      <div class="p-4 bg-white border-t border-gray-200/90 flex items-center gap-3 shrink-0">
        <input
          type="text"
          [(ngModel)]="messageText"
          (keydown.enter)="sendMessage()"
          placeholder="Type a message to {{ conciergeData.rm.name }}..."
          class="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B01E3E] focus:bg-white focus:ring-2 focus:ring-[#B01E3E]/10 transition-all text-gray-900"
        />
        <button class="w-12 h-12 rounded-xl bg-[#B01E3E] hover:bg-rose-900 text-white flex items-center justify-center text-lg font-bold transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                (click)="sendMessage()" 
                [disabled]="sending || !messageText.trim()">
          <i class="bi bi-send-fill"></i>
        </button>
      </div>

    </div>
  </div>

  <!-- State 2: Not Assigned / Upgrade Prompt -->
  <div *ngIf="!loading && (!conciergeData || !conciergeData.assigned)" class="concierge-upgrade-box bg-white rounded-2xl border border-rose-200/80 p-12 text-center shadow-md max-w-2xl mx-auto my-8">
    <div class="w-16 h-16 rounded-full bg-rose-100 text-[#B01E3E] flex items-center justify-center text-3xl mx-auto mb-4 shadow-2xs">
      <i class="bi bi-stars"></i>
    </div>
    <h3 class="font-display font-bold text-2xl text-gray-900 mb-2">Express Relationship Manager Concierge</h3>
    <p class="text-sm text-gray-600 max-w-lg mx-auto leading-relaxed mb-6">
      Subscribe to our <strong>Express Plan</strong> (`EXPRESS`) to get a dedicated, human matchmaking concierge assigned to you. Your RM hand-curates verified profiles, conducts background evaluations, connects with families, and schedules meetings on your behalf.
    </p>
    <a routerLink="/plans" class="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#B01E3E] hover:bg-rose-900 text-white font-bold text-sm no-underline shadow-md transition-all">
      <i class="bi bi-gem"></i> Upgrade to Express PRO Plan
    </a>
  </div>
</div>
